import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { GAME_RULES } from '@continuum/show-core';
import type { VineTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono } from '../ui/atoms.js';
import { ERA_SKIN, EraBackdrop, SeedSprite, SproutSprite } from './art.js';

type Dir = 'up' | 'down' | 'left' | 'right';
interface Cell {
  x: number;
  y: number;
}

const STEP: Record<Dir, Cell> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/**
 * BIOTECHNICIAN — steer a vine; it grows behind you. Gather every seed.
 *
 * Snake, which is the right shape for a post whose whole job is growing things. In
 * sealed beds the walls bite: touching one costs a seed rather than ending the run,
 * because nothing here is allowed to punish a guest.
 */
export function Vine({ task, era, onProgress, onWin, onMistake, onFrame, paused = false, s }: GameProps<VineTask>) {
  const skin = ERA_SKIN[era];
  const { cols, rows } = GAME_RULES.vine;
  const [body, setBody] = useState<Cell[]>([{ x: 2, y: Math.floor(rows / 2) }]);
  const [seed, setSeed] = useState<Cell>({ x: cols - 3, y: Math.floor(rows / 2) });
  const [got, setGot] = useState(0);
  const [bump, setBump] = useState(false);
  const dir = useRef<Dir>('right');
  const won = useRef(false);
  /** Pushing against a wall is one mistake, not one per step. */
  const against = useRef(false);
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake });
  cbs.current = { onProgress, onWin, onFrame, onMistake };
  const hold = useRef(paused);
  hold.current = paused;
  /** A swipe on the board steers too: where the finger went down, reset after every turn. */
  const swipe = useRef<{ x: number; y: number; id: number } | null>(null);

  const dropSeed = useCallback(
    (taken: Cell[]) => {
      let next: Cell;
      let guard = 0;
      do {
        next = { x: Math.floor(Math.random() * cols), y: Math.floor(Math.random() * rows) };
        guard += 1;
      } while (guard < 60 && taken.some((c) => c.x === next.x && c.y === next.y));
      setSeed(next);
    },
    [cols, rows],
  );

  useEffect(() => {
    const id = setInterval(() => {
      // Covered by an alarm or a sheet: the vine waits.
      if (hold.current) return;
      setBody((prev) => {
        const head = prev[0]!;
        const move = STEP[dir.current];
        let nx = head.x + move.x;
        let ny = head.y + move.y;

        if (task.walls) {
          if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) {
            if (!against.current) {
              against.current = true;
              cbs.current.onMistake?.();
            }
            setBump(true);
            setTimeout(() => setBump(false), 200);
            return prev; // a wall stops the vine; it counts as a mistake
          }
        } else {
          nx = (nx + cols) % cols;
          ny = (ny + rows) % rows;
        }

        against.current = false;
        const next = [{ x: nx, y: ny }, ...prev];
        if (nx === seed.x && ny === seed.y) {
          setGot((g) => {
            const total = g + 1;
            cbs.current.onProgress(Math.round((total / task.seeds) * 100));
            if (total >= task.seeds && !won.current) {
              won.current = true;
              // Not inside the state update: WELL DONE is another component's state.
              queueMicrotask(() => cbs.current.onWin());
            }
            return total;
          });
          dropSeed(next);
          return next;
        }
        next.pop();
        return next;
      });
    }, task.stepMs);
    return () => clearInterval(id);
  }, [task.stepMs, task.walls, task.seeds, cols, rows, seed, dropSeed]);

  // The vine, as cell indices, plus where the seed is.
  useEffect(() => {
    cbs.current.onFrame(
      `${body
        .slice(0, 12)
        .map((c) => c.y * cols + c.x)
        .join(',')}|s:${seed.y * cols + seed.x}`,
    );
  }, [body, seed, cols]);

  const steer = (d: Dir) => {
    if (hold.current) return;
    const opposite: Record<Dir, Dir> = { up: 'down', down: 'up', left: 'right', right: 'left' };
    if (opposite[d] === dir.current) return;
    dir.current = d;
  };

  const swipeDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* the finger already left; nothing to steer */
    }
  };
  const swipeMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const from = swipe.current;
    if (!from || from.id !== e.pointerId) return;
    const dx = e.clientX - from.x;
    const dy = e.clientY - from.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 18) return;
    steer(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
    swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
  };
  const swipeEnd = () => {
    swipe.current = null;
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div
        data-nodrag
        onPointerDown={swipeDown}
        onPointerMove={swipeMove}
        onPointerUp={swipeEnd}
        onPointerCancel={swipeEnd}
        style={{
          position: 'relative',
          width: '100%',
          touchAction: 'none',
          aspectRatio: `${cols} / ${rows}`,
          borderRadius: 12,
          border: `1px ${task.walls ? 'solid' : 'dashed'} ${bump ? '#ff2e3f' : `${task.tint}66`}`,
          background: '#080d12',
          display: 'grid',
          gridTemplateColumns: `repeat(${cols},1fr)`,
          gridTemplateRows: `repeat(${rows},1fr)`,
          gap: 1,
          padding: 3,
          overflow: 'hidden',
          transition: 'border-color .15s',
        }}
      >
        <EraBackdrop era={era} opacity={0.28} />

        {Array.from({ length: cols * rows }, (_, i) => {
          const x = i % cols;
          const y = Math.floor(i / cols);
          const partIndex = body.findIndex((c) => c.x === x && c.y === y);
          const isHead = partIndex === 0;
          const isSeed = seed.x === x && seed.y === y;
          // The head is a drawn sprout and the seed is a drawn seed; the stem behind
          // them stays plain, so the eye goes to the two things that actually matter.
          if (isHead) {
            return (
              <div key={i} style={{ padding: 1 }}>
                <SproutSprite era={era} />
              </div>
            );
          }
          if (isSeed) {
            return (
              <div key={i} style={{ filter: `drop-shadow(0 0 6px ${skin.glow})` }}>
                <SeedSprite era={era} />
              </div>
            );
          }
          return (
            <div
              key={i}
              style={{
                borderRadius: partIndex > 0 ? '50% 10% 50% 10%' : 3,
                background:
                  partIndex > 0
                    ? `${task.tint}${Math.max(30, 200 - partIndex * 18)
                        .toString(16)
                        .padStart(2, '0')}`
                    : 'rgba(255,255,255,.025)',
                transition: 'background .12s',
              }}
            />
          );
        })}
      </div>

      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '4px 12px' }}>
        <span style={{ fontFamily: mono, fontSize: 13, letterSpacing: 1.5, color: task.tint }}>{s.vineSeeds(got, task.seeds)}</span>
        <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 0.5, color: '#9db3c2' }}>{s.vineHow}</span>
      </div>
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3,64px)',
            gridTemplateRows: 'repeat(2,56px)',
            gap: 6,
            flex: 'none',
          }}
        >
          <span />
          <Pad label="▲" onPress={() => steer('up')} tint={task.tint} />
          <span />
          <Pad label="◀" onPress={() => steer('left')} tint={task.tint} />
          <Pad label="▼" onPress={() => steer('down')} tint={task.tint} />
          <Pad label="▶" onPress={() => steer('right')} tint={task.tint} />
        </div>
      </div>
    </div>
  );
}

function Pad({ label, onPress, tint }: { label: string; onPress: () => void; tint: string }) {
  return (
    <button
      onPointerDown={onPress}
      aria-label={label}
      style={{
        minHeight: 56,
        minWidth: 64,
        borderRadius: 12,
        border: `1px solid ${tint}77`,
        background: 'rgba(255,255,255,.06)',
        color: tint,
        fontSize: 20,
        touchAction: 'none',
      }}
    >
      {label}
    </button>
  );
}
